import { useCallback, useState } from 'react';
import type { CvFile } from '@components/features/screening/screening.types';
import {
  filterAllowedFiles,
  generateFileId,
  getMatchLevelFromScore,
} from '@components/features/screening/screening.constants';
import { fetcher } from '@utils/data-fetching/fetcher';
import type { components } from '@type/api/apiSchema';
import type { ApiSchema } from '@type/api/apiUtilityFn';

interface UseFileUploadProps {
  cvFiles: CvFile[];
  setCvFiles: (files: CvFile[]) => void;
  jobSlug?: string | null;
  beforeUpload?: () => Promise<void> | void;
  onUploadStart?: () => void;
  onUploadAccepted?: () => void;
}

type BulkUploadResponse = components['schemas']['CvBulkUploadOutput'];

export const useFileUpload = ({
  cvFiles,
  setCvFiles,
  jobSlug,
  beforeUpload,
  onUploadStart,
  onUploadAccepted,
}: UseFileUploadProps) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragActive(true);
    } else if (e.type === 'dragleave') {
      setIsDragActive(false);
    }
  }, []);

  const processFiles = useCallback(
    async (files: FileList) => {
      // Ensure we have a valid jobSlug before allowing uploads
      if (!jobSlug) {
        return;
      }

      const allowed = filterAllowedFiles(files);
      if (allowed.length === 0) return;

      // Call onUploadStart before setting uploading state
      onUploadStart?.();

      setIsUploading(true);

      // Add temporary entries with processing status
      const tempEntries: CvFile[] = allowed.map((file) => ({
        id: generateFileId(),
        name: file.name,
        size: file.size,
        status: 'processing' as const,
      }));
      const baseList = [...cvFiles, ...tempEntries];
      setCvFiles(baseList);

      try {
        await beforeUpload?.();

        // Build FormData for multipart upload
        const formData = new FormData();

        // Add each file to FormData with the same key 'files[]'
        // API Platform should deserialize this as an array
        allowed.forEach((file) => {
          formData.append('files[]', file);
        });

        // Call bulk upload endpoint with jobSlug
        const response = await fetcher<BulkUploadResponse>(
          `/v1/screening/jobs/${jobSlug}/cv/bulk-upload`,
          undefined,
          {
            method: 'POST',
            payload: formData,
            stringifyPayload: false,
          },
        );

        if (!response || !response.ok || !response.uploadRequests) {
          // Mark all temp entries as error
          const errored: CvFile[] = baseList.map((f) => (tempEntries.some(
            (t) => t.name === f.name && t.size === f.size && f.status === 'processing',
          )
            ? { ...f, status: 'error' as const }
            : f));
          setCvFiles(errored);
          return;
        }

        // Update files with remote IDs
        const updated: CvFile[] = [...baseList];
        response.uploadRequests.forEach((uploadRequest) => {
          // Find matching temp file by name
          const idx = updated.findIndex(
            (f) => f.status === 'processing' && f.name === uploadRequest.originalFilename,
          );

          if (idx >= 0) {
            updated[idx] = {
              ...updated[idx],
              status: 'pending',
              remoteId: String(uploadRequest.id),
              remotePath: uploadRequest.gcsPath || undefined,
            };
          }
        });
        setCvFiles(updated);

        // Notify consumers as soon as the upload request is accepted successfully.
        onUploadAccepted?.();
      } catch (error) {
        // Mark all temp entries as error
        const errored: CvFile[] = baseList.map((f) => (tempEntries.some(
          (t) => t.name === f.name && t.size === f.size && f.status === 'processing',
        )
          ? { ...f, status: 'error' as const }
          : f));
        setCvFiles(errored);
      } finally {
        setIsUploading(false);
      }
    },
    [beforeUpload, cvFiles, setCvFiles, jobSlug, onUploadStart, onUploadAccepted],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        void processFiles(e.dataTransfer.files);
      }
    },
    [processFiles],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        void processFiles(e.target.files);
      }
    },
    [processFiles],
  );

  return {
    isDragActive,
    isUploading,
    handleDrag,
    handleDrop,
    handleFileInput,
  };
};

/**
 * Map ScreeningItemDto to CvFile
 */
export const mapScreeningItemToCvFile = (
  item: ApiSchema<'ScreeningItemDto'>,
): CvFile => {
  // Map backend status to our internal status
  const mapStatus = (): CvFile['status'] => {
    const statusLower = item.status?.toLowerCase() || '';

    // Check for completed states (interview scheduled, completed, etc.)
    if (statusLower.includes('interview_scheduled')
        || statusLower.includes('interview_completed')
        || statusLower === 'completed'
        || statusLower === 'matched') {
      return 'completed';
    }

    // Check for error states
    if (statusLower.includes('failed') || statusLower.includes('error')) {
      return 'error';
    }

    // Check for matching/processing states
    if (statusLower.includes('matching') || statusLower.includes('analyzing')) {
      return 'matching';
    }

    // Check for processing states
    if (statusLower.includes('processing') || statusLower.includes('uploading')) {
      return 'processing';
    }

    // Check for queued/pending states
    if (statusLower.includes('queued') || statusLower.includes('pending')) {
      return 'pending';
    }

    // Default to completed if we have matching score (means analysis is done)
    if (item.matchingScore !== null && item.matchingScore !== undefined) {
      return 'completed';
    }

    return 'pending';
  };

  // Extract insights from matchingData object
  const extractInsights = (): { summary?: string; insights: string[] } => {
    const { matchingData } = item;

    if (!matchingData) {
      return { insights: [] };
    }
    const insights: string[] = [];
    let summary: string | undefined;

    // Extract summary separately (for KEY INSIGHTS section)
    if (matchingData.summary) {
      summary = matchingData.summary.trim();
    }

    // Extract strengths (might be a multi-line string)
    if (matchingData.strengths) {
      const strengthLines = matchingData.strengths.split('\n').filter((s: string) => s.trim());
      insights.push(...strengthLines.slice(0, 3));
    }

    return { summary, insights: insights.slice(0, 5) }; // Limit to 5 insights
  };

  // Extract interview status value
  const getInterviewStatus = (): CvFile['interviewStatus'] => {
    if (!item.interviewStatus) return undefined;
    return item.interviewStatus as CvFile['interviewStatus'];
  };

  const { summary, insights } = extractInsights();

  return {
    id: item.id && item.id.length > 0 ? item.id : generateFileId(),
    name: item.fileName || 'Unknown',
    size: item.fileSize || 0,
    status: mapStatus(),
    remoteId: item.id,
    displayName: item.name || undefined,
    headline: item.headline || undefined,
    email: item.email || undefined,
    phone: item.phone || undefined,
    score: item.finalScore ?? item.matchingScore ?? undefined,
    matchLevel: (item.finalScore ?? item.matchingScore) != null
      ? getMatchLevelFromScore(item.finalScore ?? item.matchingScore ?? 0)
      : undefined,
    matchingSummary: summary,
    insights,
    interviewScore: item.interviewScore ?? null,
    finalScore: item.finalScore ?? null,
    interviewId: item.interviewId ?? undefined,
    interviewStatus: getInterviewStatus(),
    interviewSummary: item.interviewSummary || undefined,
    sentimentScore: item.sentimentScore ?? undefined,
    sentimentLevel: item.sentimentLevel as CvFile['sentimentLevel'] || undefined,
    manualStatus: item.manualStatus as CvFile['manualStatus'] || undefined,
  };
};

export default useFileUpload;
